import Button from "@src/components/UIElements/Button";
import { getAwsAssets } from "@src/utils";
import { Image } from "@src/components/Image";
import { useRouter } from "@src/lib/router";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCourseContentTestStatsAction } from "../../store/actions";
import styles from "../../styles/TestStats.module.scss";
import TestStatsCard from "./TestStatsCard";
import TestStatsShimmer from "./TestStatsShimmer";
import TestStatsLeaderboardModal from "./TestStatsLeaderboardModal";
import usePageHeading from "@src/customHooks/usePageHeading";
import { t } from "i18next";
import handleOpenContent from "../../helpers/handleOpenContent";
import { CONTENT_TYPE } from "../../store/constants";

const TestStats = () => {
  // NEXTJS ROUTER
  const router = useRouter();

  // REDUX DISPATCH
  const dispatch = useDispatch();

  // FETCHING STATES FROM REDUCER
  const testStats = useSelector(
    (state) => state?.courseContentReducer?.apiResGET_COURSE_CONTENT_TEST_STATS
  );
  const testStatsLoading = useSelector(
    (state) =>
      state?.courseContentReducer?.loadingStateGET_COURSE_CONTENT_TEST_STATS
  );
  const isPullToRefreshTriggered = useSelector(
    (state) => state?.pullToRefreshReducer?.isPullToRefreshTriggered
  );
  const [isStatsModalOpen, setIsStatsModalOpen] = React.useState(false);
  const [statsLoading, setStatsLoading] = React.useState(false);

  const fetchTestStats = (isPullToRefreshAction = false) => {
    dispatch(
      getCourseContentTestStatsAction({
        courseId: router?.query?.courseId,
        testId: router?.query?.testId,
        contentId: router?.query?.contentId,
        isPullToRefreshAction: isPullToRefreshAction,
      })
    );
  };

  // HANDLING PULL TO REFRESH ACTION
  React.useEffect(() => {
    if (isPullToRefreshTriggered) {
      fetchTestStats(true);
    }
  }, [isPullToRefreshTriggered]);

  usePageHeading({ heading: testStats?.testName });

  // FETCHING TEST STATS
  React.useEffect(() => {
    // if courseId testId contentId
    // if first time ka sochna hai // TODO
    if (
      router?.query?.courseId &&
      router?.query?.testId &&
      router?.query?.contentId
    ) {
      fetchTestStats();
    }
  }, [router?.query?.contentId]);

  // FUNCTION TO REATTEMPT TEST
  const handleAttemptTest = (attemptUrl) => {
    attemptUrl &&
      handleOpenContent({
        contentType: CONTENT_TYPE.TEST,
        scoredMarks: testStats?.scoredMarks,
        URL: attemptUrl,
      });
  };

  const handleCloseStatsModal = () => {
    setIsStatsModalOpen(false);
  };

  const handleViewStats = () => {
    if (!router?.query?.contentId || !router?.query?.courseId || statsLoading) {
      alert('Please wait for the stats to load');
      return;
    }
    setIsStatsModalOpen(true);
  };

  return (
    <div className={styles.testStats}>
      {testStatsLoading ? (
        <TestStatsShimmer />
      ) : (
        <React.Fragment>
          <div className={styles.testStats__header}>
            {testStats?.testThumbnail && (
              <div className={styles.testStats__header__thumbnail}>
                <Image
                  src={testStats?.testThumbnail}
                  alt="thumbnail"
                  width={38}
                  height={38}
                />
              </div>
            )}
            <div className={styles.testStats__header__title}>
              {testStats?.testName}
            </div>
            {testStats?.attemptUrl && (
              <div className={styles.testStats__header__button}>
                <Button
                  small
                  outline
                  onClick={() => handleAttemptTest(testStats?.attemptUrl)}
                >
                  {t(
                    "components.tabs.content.components.testStats.index.reattemptText",
                    "Reattempt"
                  )}
                </Button>
              </div>
            )}
          </div>
          {testStats?.firstAttempt ? (
            <TestStatsCard
              cardHeading={t(
                "components.tabs.content.components.testStats.index.firstAttemptText",
                "First Attempt"
              )}
              scoredMarks={testStats?.firstAttempt?.scoredMarks}
              maxMarks={testStats?.firstAttempt?.maxMarks}
              maxScoredMarks={testStats?.firstAttempt?.maxScoredMarks}
              timeTaken={testStats?.firstAttempt?.timeTaken}
              avgTimeTaken={testStats?.firstAttempt?.avgTimeTaken}
              rank={testStats?.firstAttempt?.rank}
              yourGrade={testStats?.firstAttempt?.yourGrade}
              solutionUrl={testStats?.firstAttempt?.solutionUrl}
              solutionUrlText={testStats?.firstAttempt?.solutionUrlText}
              submissionTime={testStats?.firstAttempt?.submissionTime || testStats?.submissionTime}
              viewSubmissionText={testStats?.viewSubmissionText}
              viewSubmissionUrl={testStats?.viewSubmissionUrl}
              testId= {router?.query?.testId}
              studentTestId={testStats?.studentTestId}
              onViewStats={handleViewStats}
              isViewStatsLoading={statsLoading}
              testStats={testStats}
            />
          ) : null}
          {testStats?.lastAttempt ? (
            <TestStatsCard
              cardHeading={t(
                "components.tabs.content.components.testStats.index.latestAttemptText",
                "Latest Attempt"
              )}
              scoredMarks={testStats?.lastAttempt?.scoredMarks}
              maxMarks={testStats?.lastAttempt?.maxMarks}
              timeTaken={testStats?.lastAttempt?.timeTaken}
              projectedRank={testStats.lastAttempt.rank}
              projectedRankDiff={Math.abs(
                testStats.lastAttempt.rank - testStats.firstAttempt.rank
              )}
              projectedRankUp={
                testStats.lastAttempt.rank < testStats.firstAttempt.rank
              }
              yourGrade={testStats?.lastAttempt?.yourGrade}
              solutionUrl={testStats?.lastAttempt?.solutionUrl}
              solutionUrlText={testStats?.lastAttempt?.solutionUrlText}
              submissionTime={testStats?.lastAttempt?.submissionTime || testStats?.submissionTime}
              viewSubmissionText={testStats?.viewSubmissionText}
              viewSubmissionUrl={testStats?.viewSubmissionUrl}
              testId= {router?.query?.testId}
              studentTestId={testStats?.studentTestId}
              onViewStats={handleViewStats}
              isViewStatsLoading={statsLoading}
            />
          ) : null}
          {testStats?.attemptsLeft ? (
            <div className={styles.testStats__attemptsLeft}>
              <Image
                src={getAwsAssets("cautionIcon.svg", "content")}
                height={16}
                width={16}
              />
              <p>
                {t(
                  "components.tabs.content.components.testStats.index.youHaveText",
                  "You have"
                )}{" "}
                {testStats?.attemptsLeft}{" "}
                {t(
                  "components.tabs.content.components.testStats.index.moreAttemptsLeftText",
                  "more attempts left"
                )}
              </p>
            </div>
          ) : null}
        </React.Fragment>
      )}
      <TestStatsLeaderboardModal
        show={isStatsModalOpen}
        handleClose={handleCloseStatsModal}
        contentId={router?.query?.contentId}
        courseId={router?.query?.courseId}
        onLoadingChange={setStatsLoading}
      />
    </div>
  );
};

export default TestStats;
